import React, { useState, useEffect } from 'react';
import { Tree, Card, Button, Space, Modal, Form, Select, message, Divider } from 'antd';
import { NodeIndexOutlined, SaveOutlined, UpOutlined, DownOutlined } from '@ant-design/icons';

/**
 * 多级JSON编辑器组件
 * 支持可视化编辑多层级的JSON结构，包括拖拽排序、增删改查等功能
 * 
 * @param {Object} props
 * @param {Object} props.data - 多级数据对象
 * @param {Function} props.onDataChange - 数据变化回调函数
 * @param {Function} props.onSave - 保存数据回调函数
 * @param {boolean} props.loading - 保存加载状态
 * @param {string} props.title - 编辑器标题
 * @param {string} props.keyPrefix - 叶子节点键名前缀 (如 'cat_', 'col_')
 * @param {Array} props.items - 可选择的项目列表 (如分类、商品集等)
 * @param {string} props.itemIdField - 项目ID字段名 (默认 'id')
 * @param {string} props.itemNameField - 项目名称字段名 (默认 'name')
 * @param {string} props.leafNodeSuffix - 叶子节点显示后缀 (如 '(最终类别)', '(最终商品集)')
 * @param {string} props.addTopLevelText - 添加顶级分类按钮文本
 * @param {string} props.addTopLevelLeafText - 添加顶级叶子节点按钮文本
 * @param {string} props.selectModalTitle - 选择对话框标题
 * @param {string} props.selectLabel - 选择项目的表单标签
 * @param {string} props.selectPlaceholder - 选择项目的占位符文本
 */
const MultiLevelJsonEditor = ({
  data = {},
  onDataChange,
  onSave,
  loading = false,
  title = "多级编辑器",
  keyPrefix = "item_",
  items = [],
  itemIdField = 'id',
  itemNameField = 'name',
  leafNodeSuffix = "(最终项目)",
  addTopLevelText = "添加顶级分类",
  addTopLevelLeafText = "添加最终项目作为顶级",
  selectModalTitle = "选择要添加的项目",
  selectLabel = "项目",
  selectPlaceholder = "请选择项目"
}) => {
  const [editing, setEditing] = useState(false);
  const [expandedKeys, setExpandedKeys] = useState([]);
  const [selectModalVisible, setSelectModalVisible] = useState(false);
  const [pendingPath, setPendingPath] = useState([]);
  const [selectForm] = Form.useForm();

  useEffect(() => {
    // 初始化时展开所有节点
    const allKeys = getAllTreeKeys(data);
    setExpandedKeys(allKeys);
  }, [data]);

  // 处理保存操作
  const handleSave = async () => {
    try {
      await onSave();
      // 保存成功后自动退出编辑状态
      setEditing(false);
    } catch (error) {
      // 如果保存失败，保持编辑状态，错误处理由父组件负责
      
    }
  };

  // 转换旧格式数据：将纯数字键转换为带前缀格式
  const migrateDataFormat = (data) => {
    const newData = {};
    Object.entries(data).forEach(([key, value]) => {
      if (typeof value === 'number') {
        // 叶子节点：如果键是纯数字，转换为带前缀格式
        const newKey = /^\d+$/.test(key) ? `${keyPrefix}${key}` : key;
        newData[newKey] = value;
      } else if (typeof value === 'object' && value !== null) {
        // 非叶子节点：递归处理
        newData[key] = migrateDataFormat(value);
      } else {
        newData[key] = value;
      }
    });
    return newData;
  };

  // 转换为树形数据结构
  const convertToTreeData = (data, path = []) => {
    return Object.entries(data).map(([key, value]) => {
      const currentPath = [...path, key];
      if (typeof value === 'number') {
        // 叶子节点，key格式为 "prefix_ID"，value是ID数字
        // 通过value(ID)找到对应的项目名称
        const item = items.find(item => item[itemIdField] === value);
        const isInvalid = !item;
        
        return {
          title: item 
            ? `${item[itemNameField]} ${leafNodeSuffix}` 
            : <span style={{ color: '#ff4d4f', fontWeight: 'bold' }}>⚠️ ID: {value} (不存在) {leafNodeSuffix}</span>,
          key: currentPath.join('->'),
          isLeaf: true,
          itemId: value,
          path: currentPath,
          isInvalid: isInvalid
        };
      } else {
        // 非叶子节点
        return {
          title: key,
          key: currentPath.join('->'),
          children: convertToTreeData(value, currentPath),
          path: currentPath
        };
      }
    });
  };

  // 获取所有树节点的keys，用于展开
  const getAllTreeKeys = (data, path = []) => {
    let keys = [];
    Object.entries(data).forEach(([key, value]) => {
      const currentPath = [...path, key];
      const nodeKey = currentPath.join('->');
      if (typeof value === 'object') {
        keys.push(nodeKey);
        keys = keys.concat(getAllTreeKeys(value, currentPath));
      }
    });
    return keys;
  };

  // 更新数据
  const updateData = (path, newValue, isDelete = false) => {
    const newData = { ...data };
    let current = newData;
    
    // 导航到目标位置的父级
    for (let i = 0; i < path.length - 1; i++) {
      if (!current[path[i]]) {
        current[path[i]] = {};
      }
      current = current[path[i]];
    }
    
    if (isDelete) {
      delete current[path[path.length - 1]];
    } else {
      current[path[path.length - 1]] = newValue;
    }
    
    onDataChange && onDataChange(newData);
    
    // 自动展开所有节点
    setTimeout(() => {
      const allKeys = getAllTreeKeys(newData);
      setExpandedKeys(allKeys);
    }, 100);
  };

  // 添加分类
  const addCategory = (parentPath = []) => {
    const newKey = prompt('请输入分类名称:');
    if (newKey && newKey.trim()) {
      updateData([...parentPath, newKey.trim()], {});
    }
  };

  // 添加叶子节点
  const addLeafItem = (parentPath = []) => {
    setPendingPath(parentPath);
    selectForm.resetFields();
    setSelectModalVisible(true);
  };

  // 重命名分类
  const renameCategory = (path) => {
    const oldName = path[path.length - 1];
    const newName = prompt('请输入新的分类名称:', oldName);
    if (newName && newName.trim() && newName.trim() !== oldName) {
      const newData = { ...data };
      let current = newData;
      
      // 导航到目标位置的父级
      for (let i = 0; i < path.length - 1; i++) {
        current = current[path[i]];
      }
      
      // 获取旧节点的值
      const oldValue = current[oldName];
      
      // 删除旧键，添加新键
      delete current[oldName];
      current[newName.trim()] = oldValue;
      
      onDataChange && onDataChange(newData);
      
      // 更新展开的keys
      setTimeout(() => {
        const allKeys = getAllTreeKeys(newData);
        setExpandedKeys(allKeys);
      }, 100);
      
      message.success('重命名成功');
    }
  };

  // 处理项目选择
  const handleItemSelect = async (values) => {
    try {
      if (values.itemId && pendingPath.length >= 0) {
        // 找到选择的项目
        const selectedItem = items.find(item => item[itemIdField] === values.itemId);
        if (!selectedItem) {
          message.error('选择的项目不存在');
          return;
        }
        
        // 使用带前缀的键名，项目ID作为值
        const itemKey = `${keyPrefix}${values.itemId}`;
        const itemPath = [...pendingPath, itemKey];
        updateData(itemPath, values.itemId);
        
        setSelectModalVisible(false);
        setPendingPath([]);
        message.success('项目添加成功');
      }
    } catch (error) {
      message.error('添加项目失败: ' + (error.response?.data?.detail || error.message));
    }
  };

  // 删除节点
  const deleteNode = (path) => {
    if (window.confirm(`确定要删除 "${path.join(' -> ')}" 吗？`)) {
      updateData(path, null, true);
    }
  };

  // 处理拖拽排序
  const handleDrop = (info) => {
    const dropPos = info.node.pos.split('-');
    const dropPosition = info.dropPosition - Number(dropPos[dropPos.length - 1]);

    const dragPath = info.dragNode.path;
    const dropPath = info.node.path;

    // 不允许拖动到自己的子节点中
    if (dropPath.join('->').startsWith(dragPath.join('->') + '->')) {
      message.warning('不能将节点拖动到自己的子节点中');
      return;
    }

    // 深拷贝以避免引用问题
    const newData = JSON.parse(JSON.stringify(data));
    
    // 获取被拖动节点的值和父节点
    let dragValue;
    let dragParent = newData;
    const dragParentPath = dragPath.slice(0, -1);
    
    for (let i = 0; i < dragParentPath.length; i++) {
      dragParent = dragParent[dragParentPath[i]];
    }
    const dragKey = dragPath[dragPath.length - 1];
    dragValue = dragParent[dragKey];

    // 确定新的父节点和插入位置
    let targetParent;
    let targetParentPath;

    if (dropPosition === 0) {
      // 放入节点内部（作为第一个子节点）
      if (info.node.isLeaf) {
        message.warning('不能拖入最终项目中');
        return;
      }
      targetParentPath = dropPath;
    } else {
      // 放在节点前面或后面（同级）
      targetParentPath = dropPath.slice(0, -1);
    }

    // 获取目标父节点
    targetParent = newData;
    for (let i = 0; i < targetParentPath.length; i++) {
      targetParent = targetParent[targetParentPath[i]];
    }

    // 先从原位置删除
    delete dragParent[dragKey];

    // 重新构建目标父节点，按新顺序插入
    const entries = Object.entries(targetParent);
    const newParent = {};
    
    if (dropPosition === 0) {
      // 作为第一个子节点插入
      newParent[dragKey] = dragValue;
      entries.forEach(([key, value]) => {
        if (key !== dragKey) {
          newParent[key] = value;
        }
      });
    } else {
      // 在同级中插入
      const dropKey = dropPath[dropPath.length - 1];
      const insertBefore = dropPosition < 0;
      
      let dragInserted = false;
      entries.forEach(([key, value]) => {
        // 如果是目标位置，根据 insertBefore 决定插入顺序
        if (key === dropKey) {
          if (insertBefore) {
            // 在 dropKey 之前插入
            newParent[dragKey] = dragValue;
            newParent[key] = value;
          } else {
            // 在 dropKey 之后插入
            newParent[key] = value;
            newParent[dragKey] = dragValue;
          }
          dragInserted = true;
        } else {
          // 其他节点正常添加
          newParent[key] = value;
        }
      });
      
      // 如果到最后还没插入，说明出现了问题
      if (!dragInserted) {
        
        message.error('拖拽失败，请重试');
        return;
      }
    }

    // 更新目标父节点
    if (targetParentPath.length === 0) {
      // 顶级节点
      Object.keys(newData).forEach(key => delete newData[key]);
      Object.assign(newData, newParent);
    } else {
      let current = newData;
      for (let i = 0; i < targetParentPath.length - 1; i++) {
        current = current[targetParentPath[i]];
      }
      current[targetParentPath[targetParentPath.length - 1]] = newParent;
    }

    onDataChange && onDataChange(newData);
    
    // 保持展开状态
    setTimeout(() => {
      const allKeys = getAllTreeKeys(newData);
      setExpandedKeys(allKeys);
    }, 100);
    
    message.success('排序成功');
  };

  // 上移节点
  const moveUp = (path) => {
    const newData = { ...data };
    let parent = newData;
    
    // 获取父节点
    for (let i = 0; i < path.length - 1; i++) {
      parent = parent[path[i]];
    }
    
    const key = path[path.length - 1];
    const entries = Object.entries(parent);
    const index = entries.findIndex(([k]) => k === key);
    
    if (index <= 0) {
      message.warning('已经是第一个了');
      return;
    }
    
    // 交换位置
    [entries[index - 1], entries[index]] = [entries[index], entries[index - 1]];
    
    // 重建对象
    const newParent = {};
    entries.forEach(([k, v]) => {
      newParent[k] = v;
    });
    
    // 更新父节点
    if (path.length === 1) {
      Object.keys(newData).forEach(k => delete newData[k]);
      Object.assign(newData, newParent);
    } else {
      let current = newData;
      for (let i = 0; i < path.length - 2; i++) {
        current = current[path[i]];
      }
      current[path[path.length - 2]] = newParent;
    }
    
    onDataChange && onDataChange(newData);
    
    setTimeout(() => {
      const allKeys = getAllTreeKeys(newData);
      setExpandedKeys(allKeys);
    }, 100);
    
    message.success('上移成功');
  };

  // 下移节点
  const moveDown = (path) => {
    const newData = { ...data };
    let parent = newData;
    
    // 获取父节点
    for (let i = 0; i < path.length - 1; i++) {
      parent = parent[path[i]];
    }
    
    const key = path[path.length - 1];
    const entries = Object.entries(parent);
    const index = entries.findIndex(([k]) => k === key);
    
    if (index >= entries.length - 1) {
      message.warning('已经是最后一个了');
      return;
    }
    
    // 交换位置
    [entries[index], entries[index + 1]] = [entries[index + 1], entries[index]];
    
    // 重建对象
    const newParent = {};
    entries.forEach(([k, v]) => {
      newParent[k] = v;
    });
    
    // 更新父节点
    if (path.length === 1) {
      Object.keys(newData).forEach(k => delete newData[k]);
      Object.assign(newData, newParent);
    } else {
      let current = newData;
      for (let i = 0; i < path.length - 2; i++) {
        current = current[path[i]];
      }
      current[path[path.length - 2]] = newParent;
    }
    
    onDataChange && onDataChange(newData);
    
    setTimeout(() => {
      const allKeys = getAllTreeKeys(newData);
      setExpandedKeys(allKeys);
    }, 100);
    
    message.success('下移成功');
  };

  // 处理迁移旧格式数据
  const handleMigrateData = () => {
    const migratedData = migrateDataFormat(data);
    onDataChange && onDataChange(migratedData);
  };

  // 如果数据中存在纯数字键，提示用户迁移
  const hasOldFormatData = (data) => {
    return Object.entries(data).some(([key, value]) => {
      if (typeof value === 'number' && /^\d+$/.test(key)) {
        return true;
      }
      if (typeof value === 'object' && value !== null) {
        return hasOldFormatData(value);
      }
      return false;
    });
  };

  return (
    <Card
      title={
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <span>
            <NodeIndexOutlined style={{ marginRight: 8 }} />
            {title}
          </span>
          <Space>
            {hasOldFormatData(data) && (
              <Button
                type="dashed"
                onClick={handleMigrateData}
                style={{ borderColor: '#faad14', color: '#faad14' }}
              >
                迁移旧格式数据
              </Button>
            )}
            <Button
              type="primary"
              icon={<SaveOutlined />}
              loading={loading}
              onClick={handleSave}
              disabled={!editing}
            >
              保存
            </Button>
            <Button
              type={editing ? 'default' : 'primary'}
              onClick={() => setEditing(!editing)}
            >
              {editing ? '取消编辑' : '编辑'}
            </Button>
          </Space>
        </div>
      }
      style={{ marginTop: 24 }}
    >
      {editing ? (
        <div>
          <div style={{ marginBottom: 16 }}>
            <Space>
              <Button type="dashed" onClick={() => addCategory()}>
                {addTopLevelText}
              </Button>
              <Button type="dashed" onClick={() => addLeafItem()}>
                {addTopLevelLeafText}
              </Button>
            </Space>
          </div>
          
          <div style={{ border: '1px solid #d9d9d9', borderRadius: '6px', padding: '16px', minHeight: '300px' }}>
            {Object.keys(data).length === 0 ? (
              <div style={{ textAlign: 'center', color: '#999', padding: '50px 0' }}>
                暂无多级数据，点击上方按钮开始添加
              </div>
            ) : (
              <Tree
                treeData={convertToTreeData(data)}
                showLine
                draggable
                onDrop={handleDrop}
                expandedKeys={expandedKeys}
                onExpand={setExpandedKeys}
                titleRender={(nodeData) => (
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', width: '100%' }}>
                    <span style={{ flex: 1 }}>{nodeData.title}</span>
                    <Space size="small" style={{ marginLeft: '8px' }}>
                      <Button
                        size="small"
                        type="link"
                        icon={<UpOutlined />}
                        onClick={(e) => {
                          e.stopPropagation();
                          moveUp(nodeData.path);
                        }}
                        title="上移"
                      />
                      <Button
                        size="small"
                        type="link"
                        icon={<DownOutlined />}
                        onClick={(e) => {
                          e.stopPropagation();
                          moveDown(nodeData.path);
                        }}
                        title="下移"
                      />
                      {!nodeData.isLeaf && (
                        <>
                          <Button
                            size="small"
                            type="link"
                            onClick={(e) => {
                              e.stopPropagation();
                              addCategory(nodeData.path);
                            }}
                          >
                            添加子分类
                          </Button>
                          <Button
                            size="small"
                            type="link"
                            onClick={(e) => {
                              e.stopPropagation();
                              addLeafItem(nodeData.path);
                            }}
                          >
                            添加最终项目
                          </Button>
                          <Button
                            size="small"
                            type="link"
                            onClick={(e) => {
                              e.stopPropagation();
                              renameCategory(nodeData.path);
                            }}
                            style={{ color: '#1890ff' }}
                          >
                            重命名
                          </Button>
                        </>
                      )}
                      <Button
                        size="small"
                        type="link"
                        danger
                        onClick={(e) => {
                          e.stopPropagation();
                          deleteNode(nodeData.path);
                        }}
                      >
                        删除
                      </Button>
                      {nodeData.isInvalid && (
                        <span style={{ 
                          fontSize: '12px', 
                          color: '#ff4d4f', 
                          fontWeight: 'normal',
                          marginLeft: '4px' 
                        }}>
                          ⚠ 此ID不存在，请删除
                        </span>
                      )}
                    </Space>
                  </div>
                )}
              />
            )}
          </div>
          
          <div style={{ marginTop: 16, padding: '12px', backgroundColor: '#f0f2f5', borderRadius: '4px' }}>
            <h4>使用说明：</h4>
            <ul style={{ marginBottom: 0, paddingLeft: '20px' }}>
              <li><strong>拖拽排序</strong>：可以直接拖动节点来调整顺序或移动到其他位置</li>
              <li><strong>上移/下移</strong>：点击上下箭头按钮可以在同级中调整顺序</li>
              <li><strong>添加子分类/顶级分类</strong>：添加用于归纳整理的分类层级</li>
              <li><strong>添加最终项目</strong>：把实际的项目添加进来，这些项目下面不能再有子分类</li>
              <li><strong>重命名</strong>：修改非最终项目的分类名称</li>
              <li><strong>删除</strong>：删除当前分类及其所有子分类</li>
            </ul>
          </div>
        </div>
      ) : (
        <div>
          {Object.keys(data).length === 0 ? (
            <div style={{ textAlign: 'center', color: '#999', padding: '50px 0' }}>
              暂无多级数据
            </div>
          ) : (
            <Tree
              treeData={convertToTreeData(data)}
              showLine
              expandedKeys={expandedKeys}
              onExpand={setExpandedKeys}
              selectable={false}
            />
          )}
        </div>
      )}

      {/* 项目选择对话框 */}
      <Modal
        title={selectModalTitle}
        open={selectModalVisible}
        onCancel={() => {
          setSelectModalVisible(false);
          setPendingPath([]);
        }}
        onOk={() => selectForm.submit()}
        okText="确认添加"
        cancelText="取消"
        width={500}
      >
        <div style={{ marginBottom: 16 }}>
          {pendingPath.length > 0 && (
            <p style={{ color: '#1890ff', fontSize: '12px' }}>
              将添加到：{pendingPath.join(' → ')} → <strong>[所选项目]</strong>
            </p>
          )}
        </div>
        
        <Form
          form={selectForm}
          layout="vertical"
          onFinish={handleItemSelect}
        >
          <Form.Item
            label={selectLabel}
            name="itemId"
            rules={[{ required: true, message: `请选择一个${selectLabel}` }]}
          >
            <Select
              placeholder={selectPlaceholder}
              showSearch
              filterOption={(input, option) => {
                const text = React.Children.toArray(option?.children)
                  .map((child) => (typeof child === 'string' || typeof child === 'number' ? child : ''))
                  .join('');
                return text.toLowerCase().includes(input.toLowerCase());
              }}
              style={{ width: '100%' }}
            >
              {items.map(item => (
                <Select.Option key={item[itemIdField]} value={item[itemIdField]}>
                  {item[itemNameField]} (ID: {item[itemIdField]})
                </Select.Option>
              ))}
            </Select>
          </Form.Item>
        </Form>
      </Modal>
    </Card>
  );
};

export default MultiLevelJsonEditor;
